import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * Hooks for the /admin Payments console — the returned-payments queue
 * (backend: admin-payment-returns). The Bill & Pay cutover console + its
 * hooks were removed 2026-08-12 when the B&P integration was retired; the
 * queue is fed by the Stripe returns pipeline now (historical B&P rows
 * remain on record).
 */

export interface PaymentReturnRow {
  id: string;
  bp_return_id: string;
  returned_at: string | null;
  reason_code: string | null;
  reason_text: string | null;
  amount: number | null;
  payment_failed: boolean | null;
  bp_transaction_id: string | null;
  qbo_customer_id: string | null;
  customer_name: string | null;
  customer_id: string | null;
  payment_id: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  booked_at?: string | null;
  created_at: string;
  /** The payment ticket opened on this return, if any. */
  case: { id: string; case_number: number; status: string } | null;
}

export interface PaymentReturnsList {
  ok: true;
  unacknowledged: number;
  rows: PaymentReturnRow[];
}

/** The returned-payments queue — fed automatically by the Stripe webhooks. */
export function usePaymentReturns() {
  return useQuery<PaymentReturnsList>({
    queryKey: ['admin', 'payment-returns'],
    queryFn: () => adminFetch('admin-payment-returns'),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useAcknowledgeReturn() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: (input) =>
      adminFetch('admin-payment-returns', {
        method: 'POST',
        body: JSON.stringify({ action: 'acknowledge', id: input.id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payment-returns'] }),
  });
}

/** Clear a single return out of the queue (soft-dismiss; dedup still holds). */
export function useDismissReturn() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: (input) =>
      adminFetch('admin-payment-returns', {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss', id: input.id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payment-returns'] }),
  });
}

/** Clear every handled (acknowledged) return in one shot. */
export function useClearHandledReturns() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; cleared: number }, Error, void>({
    mutationFn: () =>
      adminFetch('admin-payment-returns', {
        method: 'POST',
        body: JSON.stringify({ action: 'clear_handled' }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'payment-returns'] }),
  });
}
